// Angular imports
import { Component, Input, OnInit, Output, EventEmitter, ChangeDetectionStrategy, inject } from '@angular/core';

// Project libraries
import { UserData, SelectorOrganization, UserProfileUpdate, MemberInfo } from '@tgx/shared/interfaces';
import { countries, extractErrorMessage, formatCountryLabel } from '@tgx/shared/utils';
import { ToastsService } from '@tgx/shared/toasts';
import { SharedLoggingService } from '@tgx/shared/logging';

// Local imports
import { BillingAccount, BillingSubTypes, Partner } from '../../interface/billing-account';
import {
  CompanyContactsInfo,
  ContactInfoData,
  ContactSectionParamData,
} from '../../interface/user-profile-contacts-info';
import { DocusignContractData, DocusingAgreementDataParam } from '../../interface/docusign';
import { Steps } from '../../enum/steps';
import { CreateBillingContractParamData } from '../../interface/onboarding';
import { ContactStepPrefix } from '../../interface/company-contacts-step-data';
import { OnboardingService } from '../../services/onboarding.service';
import { OnboardingBillingService } from '../../services/onboarding-billing.service';
import { OnboardingAgreementsService } from '../../services/onboarding-agreements.service';
import { DocusignService } from '../../services/onboarding-docusign.service';
import { OnboardingContactsService as ContactsService } from '../../services/onboarding-contacts.service';
import { OnboardingMemeberManagementService } from '../../services/onboarding-memeber-management.service';
import { MembersRole, PartnerType, PermissionsService } from '@tgx/shared/enums';
import { ContactInfo } from '../../enum/contact-info';

@Component({
  selector: 'tgx-onboarding-company-review',
  templateUrl: './onboarding-company-review.component.html',
  styleUrls: ['./onboarding-company-review.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OnboardingCompanyReviewComponent implements OnInit {
  private toast = inject(ToastsService);
  private docusignService = inject(DocusignService);
  private contactsService = inject(ContactsService);
  private onboardingService = inject(OnboardingService);
  private billingService = inject(OnboardingBillingService);
  private onboardingAgreementsService = inject(OnboardingAgreementsService);
  private onboardingMemberManagementService = inject(OnboardingMemeberManagementService);
  private loggingService = inject(SharedLoggingService);

  @Output() changeStep = new EventEmitter<Steps>();
  @Output() updateUserProfile = new EventEmitter<UserProfileUpdate>();
  @Input() isBuyer: boolean;
  @Input() isSeller: boolean;
  @Input() loggedUser: UserData;
  @Input() selectedOrg: SelectorOrganization;
  @Input() jobTitle: string;
  @Input() companyName: string;

  Steps = Steps;
  billingContact: ContactInfoData;
  apiImplementationOnboardingContact: ContactInfoData;
  techSupportContact: ContactInfoData;
  techDecisionContact: ContactInfoData;
  commercialDecisionContact: ContactInfoData;
  billingAccount: BillingAccount;

  countryString = '';
  loadingButton = false;
  contacts: MemberInfo[] = [];
  billingPartner: Partner;
  docusignAgreements: DocusignContractData[] | [];
  billingSubTypes: BillingSubTypes[];

  private MappedContactInfo = {
    [ContactInfo.API_IMPLEMENTATION]: 'Api',
    [ContactInfo.TECH_DECISION]: 'TechDecision',
    [ContactInfo.TECH_SUPPORT]: 'TechSupport',
    [ContactInfo.BILLING]: 'Billing',
  };

  private getContactInfoByType(resp: CompanyContactsInfo | string, contactType: ContactInfo): ContactInfoData | null {
    let normalizedResp: CompanyContactsInfo | null;

    if (typeof resp === 'string') {
      try {
        // Contacts info can come serialized from onboardingProperties.
        normalizedResp = JSON.parse(resp) as CompanyContactsInfo;
      } catch {
        // Invalid/corrupted JSON should not break the review screen.
        return null;
      }
    } else {
      normalizedResp = resp;
    }

    // Guard against empty payloads before resolving contact keys.
    if (!normalizedResp) {
      return null;
    }

    const legacyPrefix = this.MappedContactInfo[contactType];
    return normalizedResp[contactType] ?? normalizedResp[legacyPrefix] ?? null;
  }

  private getCountryString(code: string) {
    const cont = countries.find((country) => country.isoCode2 === code);
    return cont ? formatCountryLabel(cont) : '';
  }

  async ngOnInit() {
    this.onboardingService.scrollTop();
    this.onboardingService.setIsEditing(true);

    this.initializeBillingData();
    await this.initializeContactsData();

    this.billingPartner = await this.billingService.retrievePartner(this.selectedOrg.code);

    let signerContact: string;
    try {
      signerContact = await this.contactsService.retrieveSignerContact(this.selectedOrg.crmID);
    } catch (error) {
      this.toast.handleGatewayAndGraphqlErrors(error);
      this.loggingService.recordError('Error retrieving signer contact', {
        file: 'onboarding-company-review.component.ts',
        method: 'ngOnInit',
        description: extractErrorMessage(error),
      });
      return;
    }
    // there is always a signer contact, since it is check in signup step
    this.getAgreementsDocusign(signerContact);
    this.getBillingSubTypesContract();
  }

  private addContactsList(contacts: MemberInfo[]): void {
    contacts.forEach((contact) => {
      if (!contact?.email) {
        return;
      }

      const existingIndex = this.contacts.findIndex((c) => c.email === contact.email);
      if (existingIndex === -1) {
        this.contacts.push(contact);
        return;
      }

      const existingContact = this.contacts[existingIndex];
      this.contacts[existingIndex] = {
        ...existingContact,
        ...contact,
        role: MembersRole.VIEWER,
        newRoleInHotelX: contact.newRoleInHotelX ?? existingContact.newRoleInHotelX,
        newRoleInEntities: contact.newRoleInEntities ?? existingContact.newRoleInEntities,
        newRoleInBilling: contact.newRoleInBilling ?? existingContact.newRoleInBilling,
        newRoleInUser: contact.newRoleInUser ?? existingContact.newRoleInUser,
        newRoleInFastX: contact.newRoleInFastX ?? existingContact.newRoleInFastX,
      };
    });
  }

  private mapContactToMember(contact: MemberInfo): MemberInfo | null {
    if (!contact?.email) {
      return null;
    }

    return {
      email: contact.email,
      firstName: contact.firstName,
      lastName: contact.lastName,
      jobTitle: contact.jobTitle,
      role: MembersRole.VIEWER,
      newRoleInHotelX: contact[PermissionsService.HOTELX],
      newRoleInEntities: contact[PermissionsService.ENTITIES],
      newRoleInBilling: contact[PermissionsService.BILLING],
      newRoleInUser: contact[PermissionsService.USER],
      newRoleInFastX: contact[PermissionsService.FASTX],
    };
  }

  private getAgreementsDocusign(signerContact: string): void {
    const data: DocusingAgreementDataParam = { UserEmail: signerContact, OrgCode: this.selectedOrg.code };
    this.docusignService
      .getAgreementsList(data)
      .then((resp) => {
        this.docusignAgreements = resp;

        this.onboardingAgreementsService.checkAgreements(resp);
      })
      .catch((error) => {
        this.toast.handleGatewayAndGraphqlErrors(error);
        this.loggingService.recordError('Error retrieving docusign agreements', {
          file: 'onboarding-company-review.component.ts',
          method: 'getAgreementsDocusign',
          description: extractErrorMessage(error),
        });
      });
  }

  private getBillingSubTypesContract(): void {
    this.billingService
      .retrieveBillingSubTypes()
      .then((resp) => {
        this.billingSubTypes = resp;
      })
      .catch((err) => {
        this.toast.addToast('Error receive billing subtypes', 'bg-danger', err);
      });
  }

  private initializeBillingData(): void {
    this.billingService
      .getBillingAccounts(this.selectedOrg.code)
      .then((accounts) => {
        this.billingAccount = accounts[0];
        this.countryString = this.getCountryString(this.billingAccount?.countryCode);
      })
      .catch((error) => {
        this.toast.addToast('Error receive billing accounts', 'bg-danger', error);
      });
  }

  private async initializeContactsData(): Promise<void> {
    this.contacts = [];

    let contactsInfo: CompanyContactsInfo | string = this.loggedUser?.onboardingProperties?.contactsInfo;

    try {
      const latestContactsInfo = await this.onboardingService.getUserProfileContactInfo(this.loggedUser.email);
      if (latestContactsInfo && Object.keys(latestContactsInfo).length > 0) {
        contactsInfo = latestContactsInfo;
      }
    } catch {
      // Already logged by GqlService; keep fallback to logged user onboarding properties.
    }

    if (contactsInfo) {
      const apiContact = this.getContactInfoByType(contactsInfo, ContactInfo.API_IMPLEMENTATION);
      const billingContact = this.getContactInfoByType(contactsInfo, ContactInfo.BILLING);
      const techDecisionContact = this.getContactInfoByType(contactsInfo, ContactInfo.TECH_DECISION);
      const techSupportContact = this.getContactInfoByType(contactsInfo, ContactInfo.TECH_SUPPORT);

      if (apiContact) {
        this.apiImplementationOnboardingContact = {
          email: apiContact.email,
          firstName: apiContact.firstName,
          lastName: apiContact.lastName,
        };
      }
      if (billingContact) {
        this.billingContact = {
          email: billingContact.email,
          firstName: billingContact.firstName,
          lastName: billingContact.lastName,
        };
      }
      if (techDecisionContact) {
        this.techDecisionContact = {
          email: techDecisionContact.email,
          firstName: techDecisionContact.firstName,
          lastName: techDecisionContact.lastName,
        };
      }
      if (techSupportContact) {
        this.techSupportContact = {
          email: techSupportContact.email,
          firstName: techSupportContact.firstName,
          lastName: techSupportContact.lastName,
        };
      }
      this.commercialDecisionContact = {
        firstName: this.loggedUser.firstName,
        lastName: this.loggedUser.lastName,
        email: this.loggedUser.email,
      };

      const contactsToAdd = [
        this.mapContactToMember(apiContact),
        this.mapContactToMember(billingContact),
        this.mapContactToMember(techDecisionContact),
        this.mapContactToMember(techSupportContact),
      ].filter((contact): contact is MemberInfo => !!contact);

      this.addContactsList(contactsToAdd);
    }
  }

  protected onEditSection(step: Steps) {
    this.changeStep.emit(step);
  }

  protected onEditContactType(contactType: ContactStepPrefix) {
    this.onboardingService.setSelectedContactTypeToEdit(contactType);
    this.changeStep.emit(Steps.COMPANY_CONTACTS);
  }

  protected async submitReview() {
    if (!this.loadingButton) {
      try {
        this.loadingButton = true;
        const memberResults = await Promise.allSettled(
          this.contacts.map((contact) => this.onboardingMemberManagementService.onAddMember(contact)),
        );
        if (memberResults.some((result) => result.status === 'rejected')) {
          return; // Each failed invitation is already toasted by onAddMember.
        }

        const contactsParamData: ContactSectionParamData = {
          commercialDecisionContact: this.commercialDecisionContact,
          techDecisionContact: this.techDecisionContact,
          techSupportContact: this.techSupportContact,
        };

        const createBillingContractParamData: CreateBillingContractParamData = {
          agreements: this.docusignAgreements,
          billingPartner: this.billingPartner,
          billingSubTypes: this.billingSubTypes,
        };

        const results = await Promise.allSettled([
          this.contactsService.onAddCompanyContacts(contactsParamData),
          this.onboardingAgreementsService.createBillingContract(createBillingContractParamData),
          this.contactsService.addBillingContact(this.billingPartner, this.billingContact),
        ]);

        const errorMessages: string[] = [];

        results.forEach((result) => {
          if (result.status === 'rejected') {
            errorMessages.push(result.reason);
          }
        });

        if (errorMessages.length > 0) {
          this.toast.addToast('Error notification', 'bg-danger', errorMessages.join('\n'));
        } else {
          try {
            await this.finishHubspotRegistration();
          } catch (error) {
            this.toast.handleGatewayAndGraphqlErrors(error);
            this.loggingService.recordError('Error finishing Hubspot registration', {
              file: 'onboarding-company-review.component.ts',
              method: 'submitReview',
              description: extractErrorMessage(error),
            });
            return;
          }

          this.finishUserOnboarding();
        }
      } finally {
        this.loadingButton = false;
      }
    }
  }

  private finishUserOnboarding() {
    const date = new Date().toISOString();
    this.updateUserProfile.emit({ onboardingProperties: { finishedAt: date } });
    this.changeStep.emit(Steps.CONFIRM);
  }

  private async finishHubspotRegistration() {
    const partnerTypes: PartnerType[] = [];
    if (this.isBuyer) partnerTypes.push(PartnerType.BUYER);
    if (this.isSeller) partnerTypes.push(PartnerType.SELLER);

    await this.onboardingService.finishHubspotRegistration(this.selectedOrg.crmID, partnerTypes);
  }
}
